import { Component, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { DwOrmDataService, DwOrmDataServiceToken, DwModalService, DwModalConfig, DwModalSize, DwModalButtonStandardConfigs } from '@devwareapps/devware-cap';
import { Subscription, combineLatest } from 'rxjs';
import { take, switchMap } from 'rxjs/operators';
import { TeamJoinedModalComponent } from './team-joined-modal.component';
import { Inject } from '@angular/core';
import { TeamMembershipStatus } from '../../models/team.models';
import { AppMetaData, ChallengeTeamEntity, ChallengeTeamMemberEntity, ChallengeTeamJoinRequestEntity, ChallengeTeamJoinRequestStatusAllItems } from '../../../../meta-data/app-meta-data.service';

@Component({
    selector: 'app-team-join-cta',
    templateUrl: './team-join-cta.component.html',
    styleUrls: ['./team-join-cta.component.scss']
})
export class TeamJoinCtaComponent implements OnDestroy {

    @Input() team: ChallengeTeamEntity;
    @Input() membershipStatus: TeamMembershipStatus;
    @Input() userChallengeId: number;
    @Input() canJoin = false;
    @Output() joined = new EventEmitter<string>();
    @Output() refreshRequested = new EventEmitter<void>();

    submitting = false;
    error = '';
    uncertain = false;
    private subscriptions = new Subscription();



    constructor(
        @Inject(DwOrmDataServiceToken) private ormDataService: DwOrmDataService,
        private modal: DwModalService,
    ) {}

    get isLocked(): boolean {
        return !!this.team?.TeamLocked;
    }

    get requiresApproval(): boolean {
        return !!this.team?.RequestApprovalToJoin;
    }

    joinOrRequest() {
        if (this.submitting || this.uncertain || !this.canJoin || !this.userChallengeId || !this.team ||
            this.isLocked || this.membershipStatus?.isMember || this.membershipStatus?.hasPendingRequest) { return; }
        this.submitting = true;
        this.error = '';

        // Status 1 = Pending; interceptor will auto-approve if RequestApprovalToJoin=false
        const newRequest = AppMetaData.ChallengeTeamJoinRequest.CreateEntity();
        newRequest.ChallengeTeamId = this.team.ChallengeTeamId;
        newRequest.UserChallengeId = this.userChallengeId;
        newRequest.ChallengeTeamJoinRequestStatusId = ChallengeTeamJoinRequestStatusAllItems.Pending;

        // Read the authoritative outcome rather than assuming the requested status was saved.
        this.subscriptions.add(this.ormDataService.saveEntity(newRequest).pipe(take(1), switchMap(() => {
            const query = AppMetaData.ChallengeTeamMember.CreateQueryBuilder()
                .addFilterAnd(f => f.Equal(a => a.ChallengeTeamId, this.team.ChallengeTeamId))
                .addFilterAnd(f => f.Equal(a => a.UserChallengeId, this.userChallengeId));
            const requests = AppMetaData.ChallengeTeamJoinRequest.CreateQueryBuilder()
                .addFilterAnd(f => f.Equal(a => a.ChallengeTeamId, this.team.ChallengeTeamId))
                .addFilterAnd(f => f.Equal(a => a.UserChallengeId, this.userChallengeId))
                .addFilterAnd(f => f.Equal(a => a.ChallengeTeamJoinRequestStatusId, ChallengeTeamJoinRequestStatusAllItems.Pending));
            return combineLatest([
                this.ormDataService.executeQuery<ChallengeTeamMemberEntity>(query),
                this.ormDataService.executeQuery<ChallengeTeamJoinRequestEntity>(requests)
            ]).pipe(take(1));
        })).subscribe(([members, requests]) => {
            this.submitting = false;
            if (members.length) {
                const config = new DwModalConfig<{ teamName: string }>();
                config.title = 'Welcome to the team!';
                config.component = TeamJoinedModalComponent;
                config.data = { teamName: this.team.TeamName };
                config.modalSize = DwModalSize.responsive;
                config.buttons = [{ ...DwModalButtonStandardConfigs.instance.closeButton, buttonText: 'Let’s streak!', buttonClass: 'btn btn-primary' }];
                this.modal.showModal(config).pipe(take(1)).subscribe();
                this.joined.emit(`You’re now on ${this.team.TeamName}!`);
            } else if (requests.length) {
                this.joined.emit('Your request was sent. A team captain will review it.');
            } else {
                this.showUnconfirmed();
            }
        }, () => {
            this.showUnconfirmed();
        }));
    }
    private showUnconfirmed(): void {
        this.submitting = false;
        this.uncertain = true;
        this.error = 'We couldn’t confirm your team status. Refresh before trying again.';
    }
    ngOnDestroy(): void { this.subscriptions.unsubscribe(); }
}
